import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  Chip,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import * as XLSX from 'xlsx';
import './BulkActionsToolbar.css';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const BulkActionsToolbar = ({
  selectedCustomers,
  onClearSelection,
  onBulkUpdateServiceDay,
  onBulkDeactivate,
  onBulkDelete,
  onExportComplete
}) => {
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [serviceDayDialogOpen, setServiceDayDialogOpen] = useState(false);
  const [selectedServiceDay, setSelectedServiceDay] = useState('');
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Check if selected customers are all active, all inactive, or mixed
  const allActive = selectedCustomers.length > 0 && selectedCustomers.every(c => c.status === 'Active');
  const allInactive = selectedCustomers.length > 0 && selectedCustomers.every(c => c.status === 'Inactive');
  const hasMixed = selectedCustomers.length > 0 && !allActive && !allInactive;

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  // Export to CSV
  const handleExportCSV = () => {
    const csvData = selectedCustomers.map(customer => ({
      'Customer Name': customer.name,
      'Contact': customer.contact,
      'Address': customer.address,
      'Service Day': customer.serviceDay || '',
      'Status': customer.status,
      'City': customer.custObject?.custCity || '',
      'State': customer.custObject?.custState || '',
      'ZIP': customer.custObject?.custZip || '',
      'Email': customer.custObject?.custEmail || '',
    }));

    // Convert to CSV
    const headers = Object.keys(csvData[0]);
    const csvRows = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(header => {
          const value = row[header] || '';
          // Escape quotes and wrap in quotes if contains comma
          return value.toString().includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
        }).join(',')
      )
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `customers_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleExportClose();
    if (onExportComplete) onExportComplete('CSV');
  };

  // Export to Excel
  const handleExportExcel = () => {
    const excelData = selectedCustomers.map(customer => ({
      'Customer Name': customer.name,
      'Contact': customer.contact,
      'Address': customer.address,
      'Service Day': customer.serviceDay || '',
      'Status': customer.status,
      'City': customer.custObject?.custCity || '',
      'State': customer.custObject?.custState || '',
      'ZIP': customer.custObject?.custZip || '',
      'Email': customer.custObject?.custEmail || '',
    }));

    // Create workbook
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Customers');

    // Auto-size columns
    const maxWidth = 50;
    const colWidths = Object.keys(excelData[0] || {}).map(key => ({
      wch: Math.min(
        Math.max(
          key.length,
          ...excelData.map(row => (row[key] || '').toString().length)
        ),
        maxWidth
      )
    }));
    ws['!cols'] = colWidths;

    // Download
    XLSX.writeFile(wb, `customers_export_${new Date().toISOString().split('T')[0]}.xlsx`);

    handleExportClose();
    if (onExportComplete) onExportComplete('Excel');
  };

  // Bulk update service day
  const handleServiceDayUpdate = () => {
    if (selectedServiceDay && onBulkUpdateServiceDay) {
      onBulkUpdateServiceDay(selectedCustomers, selectedServiceDay);
      setServiceDayDialogOpen(false);
      setSelectedServiceDay('');
    }
  };

  // Bulk deactivate
  const handleDeactivateConfirm = () => {
    if (onBulkDeactivate) {
      onBulkDeactivate(selectedCustomers);
      setDeactivateDialogOpen(false);
    }
  };

  // Bulk delete (permanent)
  const handleDeleteConfirm = () => {
    if (onBulkDelete) {
      onBulkDelete(selectedCustomers);
      setDeleteDialogOpen(false);
    }
  };

  if (selectedCustomers.length === 0) {
    return null;
  }

  return (
    <>
      <Fade in={true}>
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            mb: 2,
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#E3F2FD',
            borderLeft: '4px solid #2196F3',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1.5, sm: 2 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <Chip
              label={`${selectedCustomers.length} selected`}
              color="primary"
              size={window.innerWidth < 600 ? 'small' : 'medium'}
              sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.9rem' } }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}>
              Bulk actions available
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap', justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
            {/* Export Button */}
            <Tooltip title="Export selected customers">
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                onClick={handleExportClick}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                sx={{
                  backgroundColor: '#10B981',
                  '&:hover': { backgroundColor: '#059669' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  minWidth: { xs: 'auto', sm: '64px' },
                  flex: { xs: '1', sm: '0 1 auto' }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Export</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Export</Box>
              </Button>
            </Tooltip>

            {/* Update Service Day */}
            <Tooltip title="Update service day for selected customers">
              <Button
                variant="contained"
                startIcon={<EditIcon sx={{ fontSize: { xs: 16, sm: 20 }, display: { xs: 'none', sm: 'inline-flex' } }} />}
                onClick={() => setServiceDayDialogOpen(true)}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                sx={{
                  backgroundColor: '#2196F3',
                  '&:hover': { backgroundColor: '#1976D2' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  minWidth: { xs: 'auto', sm: '64px' },
                  flex: { xs: '1', sm: '0 1 auto' }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Update Service Day</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Update</Box>
              </Button>
            </Tooltip>

            {/* Deactivate/Delete Customers */}
            {allActive && (
              <Tooltip title="Deactivate selected customers">
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon sx={{ fontSize: { xs: 16, sm: 20 }, display: { xs: 'none', sm: 'inline-flex' } }} />}
                  onClick={() => setDeactivateDialogOpen(true)}
                  size={window.innerWidth < 600 ? 'small' : 'medium'}
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '4px 8px', sm: '6px 16px' },
                    minWidth: { xs: 'auto', sm: '64px' },
                    flex: { xs: '1', sm: '0 1 auto' }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Deactivate</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Deactivate</Box>
                </Button>
              </Tooltip>
            )}

            {allInactive && (
              <Tooltip title="Permanently delete selected customers">
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon sx={{ fontSize: { xs: 16, sm: 20 }, display: { xs: 'none', sm: 'inline-flex' } }} />}
                  onClick={() => setDeleteDialogOpen(true)}
                  size={window.innerWidth < 600 ? 'small' : 'medium'}
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '4px 8px', sm: '6px 16px' },
                    minWidth: { xs: 'auto', sm: '64px' },
                    flex: { xs: '1', sm: '0 1 auto' }
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Delete</Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Delete</Box>
                </Button>
              </Tooltip>
            )}

            {hasMixed && (
              <Tooltip title="Cannot perform bulk action on mixed active/inactive customers">
                <span>
                  <Button
                    variant="contained"
                    color="error"
                    disabled
                    startIcon={<DeleteIcon sx={{ fontSize: { xs: 16, sm: 20 }, display: { xs: 'none', sm: 'inline-flex' } }} />}
                    size={window.innerWidth < 600 ? 'small' : 'medium'}
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '4px 8px', sm: '6px 16px' },
                      minWidth: { xs: 'auto', sm: '64px' },
                      flex: { xs: '1', sm: '0 1 auto' }
                    }}
                  >
                    <Box component="span">Mixed Selection</Box>
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Clear Selection */}
            <Tooltip title="Clear selection">
              <IconButton
                onClick={onClearSelection}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                sx={{
                  backgroundColor: 'white',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  padding: { xs: '6px', sm: '8px' }
                }}
              >
                <CloseIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      </Fade>

      {/* Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={handleExportCSV}>
          <FileDownloadIcon sx={{ mr: 1 }} />
          Export as CSV
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <FileDownloadIcon sx={{ mr: 1 }} />
          Export as Excel
        </MenuItem>
      </Menu>

      {/* Service Day Update Dialog */}
      <Dialog
        open={serviceDayDialogOpen}
        onClose={() => setServiceDayDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Update Service Day</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: 'auto', sm: 400 }, pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Update service day for {selectedCustomers.length} selected customer(s)
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedServiceDay}
              onChange={(e) => setSelectedServiceDay(e.target.value)}
              displayEmpty
              size={window.innerWidth < 600 ? 'small' : 'medium'}
            >
              <MenuItem value="" disabled>
                Select Service Day
              </MenuItem>
              {DAYS_OF_WEEK.map(day => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1 }, gap: 1 }}>
          <Button
            onClick={() => setServiceDayDialogOpen(false)}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Cancel
          </Button>
          <Button
            onClick={handleServiceDayUpdate}
            variant="contained"
            disabled={!selectedServiceDay}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={deactivateDialogOpen}
        onClose={() => setDeactivateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Confirm Bulk Deactivation</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Are you sure you want to deactivate {selectedCustomers.length} customer(s)?
          </Typography>
          <Typography variant="body2" color="error" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            This action will mark the selected customers as inactive. They will not appear in the default active customer list.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1 }, gap: 1 }}>
          <Button
            onClick={() => setDeactivateDialogOpen(false)}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeactivateConfirm}
            variant="contained"
            color="error"
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: '#ef4444' }}>
          ⚠️ Confirm Permanent Deletion
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 'bold' }}>
            Are you sure you want to <strong>permanently delete</strong> {selectedCustomers.length} customer(s)?
          </Typography>
          <Typography variant="body2" color="error" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 'bold', mb: 1 }}>
            WARNING: This action cannot be undone!
          </Typography>
          <Typography variant="body2" color="error" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            All customer information, including pool details and service history, will be permanently deleted from the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1 }, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
            sx={{ fontWeight: 'bold' }}
          >
            Yes, Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkActionsToolbar;
